// ============================================================================
// VERSION CONTRACT - ARCHITECTURE LOCK
// ============================================================================

/**
 * QA RULES VERSION CONTRACT
 * 
 * This is a frozen architectural invariant. Any modification to the QA rules
 * taxonomy MUST:
 * 1. Increment QUALITY_RULE_VERSION
 * 2. Update QUALITY_RULE_COUNT
 * 3. Update parity tests explicitly
 * 4. Document change in RFC or PR description
 * 
 * Violation of this contract will cause CI failure.
 */
export const QUALITY_RULE_VERSION = 'v1.4' as const;
export const QUALITY_RULE_COUNT = 32 as const;

// ============================================================================
// EXECUTION MODEL POLICY
// ============================================================================

/**
 * QA EXECUTION MODEL (Read-Only Policy)
 * 
 * 1. QA rules are PURE DETECTION - they never mutate data
 * 2. All fixes happen via:
 *    - SQL migration (schema-level corrections)
 *    - UI edit (manual corrections)
 *    - Field-scoped AI optimization (targeted regeneration)
 * 3. AI optimization MUST be field-scoped, never full-row
 * 4. This policy is enforced at runtime via assertRuleCountIntegrity()
 * 
 * Any workflow that violates this model is considered a defect.
 */

// ============================================================================
// QUALITY RULES - SINGLE SOURCE OF TRUTH
// ============================================================================

/**
 * Quality Rules - SINGLE SOURCE OF TRUTH
 * 
 * All QA validation rules defined in one place.
 * Frontend (qualityConfig.ts) and backend (quality-config.ts) import from here.
 * 
 * RULE COUNT: 33 total (19 Error + 11 Warning + 3 Info)
 * 
 * Categories:
 * - 20 single-record (per-field validation)
 * - 9 cross-record (multi-record analysis)
 * - 2 backend (database-level checks)
 * 
 * To add a new rule:
 * 1. Add to QualityIssueType union below
 * 2. Add entry to QUALITY_RULES array
 * 3. Run parity tests: npm run test src/config/__tests__/qualityConfigParity.test.ts
 * 
 * @see docs/QUALITY_GOVERNANCE.md
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * All quality issue types in the system (33 total)
 */
export type QualityIssueType =
  // === ERROR - Single Record (13) ===
  | 'null_field'
  | 'empty_field'
  | 'placeholder_detected'
  | 'encoding_error'
  | 'control_characters'
  | 'rtl_bidi_corruption'
  | 'invalid_slug_format'
  | 'script_mismatch'
  | 'pure_ascii_non_latin'
  | 'invalid_faq_structure'
  | 'hreflang_gap'
  | 'model_output_corruption'
  | 'excerpt_length'
  // === ERROR - Cross Record (4) ===
  | 'hreflang_incomplete'
  | 'missing_en_source'
  | 'orphan_translation'
  | 'duplicate_slug'
  // === ERROR - Backend (2) ===
  | 'cross_entity_collision'
  | 'broken_entity_reference'
  // === WARNING - Single Record (5) ===
  | 'too_short'
  | 'too_long'
  | 'english_fallback'
  | 'low_script_ratio'
  | 'empty_faq_items'
  // === WARNING - Cross Record (4) ===
  | 'stale_translation'
  | 'cross_locale_copy'
  | 'incomplete_seed'
  | 'location_locale_mismatch'
  | 'topic_orphan'
  // === INFO - Single Record (3) ===
  | 'invalid_json'
  | 'punctuation_mismatch'
  | 'word_count_deviation';

/**
 * Severity levels for quality issues
 */
export type QualityIssueSeverity = 'error' | 'warning' | 'info';

/**
 * Category for grouping issues by check type
 */
export type QualityIssueCategory = 'single-record' | 'cross-record' | 'backend';

/**
 * Fix action types for automated remediation
 */
export type QualityFixAction = 'regenerate' | 'translate' | 'seed' | 'manual' | 'auto' | 'delete' | 'none';

/**
 * Complete quality rule definition
 */
export interface QualityRule {
  readonly type: QualityIssueType;
  readonly severity: QualityIssueSeverity;
  readonly category: QualityIssueCategory;
  readonly label: string;
  readonly description: string;
  readonly fixable: boolean;
  readonly fixAction: QualityFixAction;
}

// ============================================================================
// QUALITY RULES - CANONICAL DEFINITION (33 rules)
// ============================================================================

/**
 * Complete QA Rules Taxonomy
 * 
 * Distribution:
 * - 19 ERROR: 13 single-record + 4 cross-record + 2 backend
 * - 11 WARNING: 6 single-record + 5 cross-record
 * - 3 INFO: 3 single-record
 */
export const QUALITY_RULES: readonly QualityRule[] = [
  // =========================================================================
  // ERROR - Single Record (13)
  // =========================================================================
  {
    type: 'null_field',
    severity: 'error',
    category: 'single-record',
    label: 'Missing Field',
    description: 'Required field is NULL',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'empty_field',
    severity: 'error',
    category: 'single-record',
    label: 'Empty Field',
    description: 'Field exists but is empty',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'placeholder_detected',
    severity: 'error',
    category: 'single-record',
    label: 'Placeholder Text',
    description: 'Contains TODO/Lorem ipsum',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'encoding_error',
    severity: 'error',
    category: 'single-record',
    label: 'Encoding Error',
    description: 'Mojibake or escaped Unicode',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'control_characters',
    severity: 'error',
    category: 'single-record',
    label: 'Control Characters',
    description: 'Invalid control chars',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'rtl_bidi_corruption',
    severity: 'error',
    category: 'single-record',
    label: 'RTL Corruption',
    description: 'Stray BiDi markers',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'invalid_slug_format',
    severity: 'error',
    category: 'single-record',
    label: 'Invalid Slug',
    description: 'Fails slug regex',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'script_mismatch',
    severity: 'error',
    category: 'single-record',
    label: 'Script Mismatch',
    description: 'Latin-only for non-Latin locale',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'pure_ascii_non_latin',
    severity: 'error',
    category: 'single-record',
    label: 'Pure ASCII',
    description: '100% ASCII for CJK/RTL locale',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'invalid_faq_structure',
    severity: 'error',
    category: 'single-record',
    label: 'Invalid FAQ',
    description: 'Malformed faq_items JSONB',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'hreflang_gap',
    severity: 'error',
    category: 'single-record',
    label: 'Hreflang Gap',
    description: 'Missing translated_slug for SEO entity',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'model_output_corruption',
    severity: 'error',
    category: 'single-record',
    label: 'Model Corruption',
    description: 'AI returned garbage/debug output',
    fixable: false,  // Requires retry, not inline fix
    fixAction: 'regenerate',
  },
  {
    type: 'excerpt_length',
    severity: 'error',
    category: 'single-record',
    label: 'Excerpt Length',
    description: 'Excerpt outside proportional bounds',
    fixable: true,
    fixAction: 'regenerate',
  },

  // =========================================================================
  // ERROR - Cross Record (4)
  // =========================================================================
  {
    type: 'hreflang_incomplete',
    severity: 'error',
    category: 'cross-record',
    label: 'Hreflang Incomplete',
    description: 'Missing slugs for some locales',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'missing_en_source',
    severity: 'error',
    category: 'cross-record',
    label: 'Missing EN Source',
    description: 'No English source row',
    fixable: true,
    fixAction: 'seed',
  },
  {
    type: 'orphan_translation',
    severity: 'error',
    category: 'cross-record',
    label: 'Orphan Translation',
    description: 'Non-EN without EN source',
    fixable: false,
    fixAction: 'manual',
  },
  {
    type: 'duplicate_slug',
    severity: 'error',
    category: 'cross-record',
    label: 'Duplicate Slug',
    description: 'Same slug in entity+locale',
    fixable: true,
    fixAction: 'regenerate',
  },

  // =========================================================================
  // ERROR - Backend (2)
  // =========================================================================
  {
    type: 'cross_entity_collision',
    severity: 'error',
    category: 'backend',
    label: 'Entity Collision',
    description: 'Same slug different entity',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'broken_entity_reference',
    severity: 'error',
    category: 'backend',
    label: 'Broken Reference',
    description: 'Invalid FK reference',
    fixable: false,
    fixAction: 'manual',
  },

  // =========================================================================
  // WARNING - Single Record (6)
  // =========================================================================
  {
    type: 'too_short',
    severity: 'warning',
    category: 'single-record',
    label: 'Too Short',
    description: 'Below min length',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'too_long',
    severity: 'warning',
    category: 'single-record',
    label: 'Too Long',
    description: 'Exceeds max length',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'english_fallback',
    severity: 'warning',
    category: 'single-record',
    label: 'English Fallback',
    description: 'Identical to EN source',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'low_script_ratio',
    severity: 'warning',
    category: 'single-record',
    label: 'Low Script Ratio',
    description: 'Insufficient native script',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'empty_faq_items',
    severity: 'warning',
    category: 'single-record',
    label: 'Empty FAQ',
    description: '<3 Q&A pairs',
    fixable: true,
    fixAction: 'regenerate',
  },

  // =========================================================================
  // WARNING - Cross Record (5)
  // NOTE: missing_keywords REMOVED - meta_keywords deprecated by Google since 2009
  // WARNING - Cross Record (5)
  // =========================================================================
  {
    type: 'stale_translation',
    severity: 'warning',
    category: 'cross-record',
    label: 'Stale Translation',
    description: 'EN source changed',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'cross_locale_copy',
    severity: 'warning',
    category: 'cross-record',
    label: 'Cross-Locale Copy',
    description: 'Same text in 3+ locales',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'incomplete_seed',
    severity: 'warning',
    category: 'cross-record',
    label: 'Incomplete Seed',
    description: 'Missing translated_slug/name (EN source)',
    fixable: true,
    fixAction: 'seed',
  },
  {
    type: 'location_locale_mismatch',
    severity: 'warning',
    category: 'cross-record',
    label: 'Locale Mismatch',
    description: 'Content not in native locale',
    fixable: true,
    fixAction: 'translate',
  },
  {
    type: 'topic_orphan',
    severity: 'warning',
    category: 'cross-record',
    label: 'Topic Orphan',
    description: 'References deleted topic',
    fixable: false,
    fixAction: 'manual',
  },

  // =========================================================================
  // INFO - Single Record (3)
  // =========================================================================
  {
    type: 'invalid_json',
    severity: 'info',
    category: 'single-record',
    label: 'Invalid JSON',
    description: 'Malformed JSONB',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'punctuation_mismatch',
    severity: 'info',
    category: 'single-record',
    label: 'Punctuation',
    description: 'Wrong punctuation style',
    fixable: true,
    fixAction: 'regenerate',
  },
  {
    type: 'word_count_deviation',
    severity: 'info',
    category: 'single-record',
    label: 'Length Deviation',
    description: 'Translation too long/short',
    fixable: false,
    fixAction: 'manual',
  },
] as const;

// ============================================================================
// DERIVED LOOKUPS (O(1) access)
// ============================================================================

/**
 * Map from issue type to full rule definition
 */
export const QUALITY_RULES_BY_TYPE: Record<QualityIssueType, QualityRule> = 
  Object.fromEntries(QUALITY_RULES.map(r => [r.type, r])) as Record<QualityIssueType, QualityRule>;

/**
 * Get all issue types as array
 */
export const ALL_ISSUE_TYPES: readonly QualityIssueType[] = 
  QUALITY_RULES.map(r => r.type);

/**
 * Issue types that can be fixed via AI optimization
 * Derived from rules where fixable=true
 */
export const FIXABLE_ISSUE_TYPES: readonly QualityIssueType[] = 
  QUALITY_RULES.filter(r => r.fixable).map(r => r.type);

export type FixableIssueType = typeof FIXABLE_ISSUE_TYPES[number];

// ============================================================================
// SEVERITY COUNTS - Compile-time constants
// ============================================================================

/**
 * Rule counts by severity and category
 * These are compile-time constants for documentation and testing
 */
export const QUALITY_RULE_COUNTS = {
  total: 32,
  bySeverity: {
    error: 19,
    warning: 10,
    info: 3,
  },
  byCategory: {
    singleRecord: 21,
    crossRecord: 9,
    backend: 2,
  },
  byBreakdown: {
    errorSingleRecord: 13,
    errorCrossRecord: 4,
    errorBackend: 2,
    warningSingleRecord: 5,
    warningCrossRecord: 5,
    infoSingleRecord: 3,
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get severity for an issue type
 */
export function getIssueSeverity(issueType: QualityIssueType): QualityIssueSeverity {
  return QUALITY_RULES_BY_TYPE[issueType]?.severity ?? 'info';
}

/**
 * Get label for an issue type
 */
export function getIssueLabel(issueType: QualityIssueType): string {
  return QUALITY_RULES_BY_TYPE[issueType]?.label ?? issueType;
}

/**
 * Get full rule definition for an issue type
 */
export function getQualityRule(issueType: QualityIssueType): QualityRule | undefined {
  return QUALITY_RULES_BY_TYPE[issueType];
}

/**
 * Check if an issue type is fixable
 */
export function isFixable(issueType: QualityIssueType): boolean {
  return QUALITY_RULES_BY_TYPE[issueType]?.fixable ?? false;
}

/**
 * Get all issues of a specific severity
 */
export function getIssuesBySeverity(severity: QualityIssueSeverity): QualityRule[] {
  return QUALITY_RULES.filter(r => r.severity === severity);
}

/**
 * Get all issues of a specific category
 */
export function getIssuesByCategory(category: QualityIssueCategory): QualityRule[] {
  return QUALITY_RULES.filter(r => r.category === category);
}

// ============================================================================
// RUNTIME INTEGRITY GUARD
// ============================================================================

/**
 * Assert QA system integrity at runtime.
 * Call this at QA entrypoints as a last-resort panic guard.
 * 
 * @throws Error if rule count doesn't match frozen constant
 */
export function assertRuleCountIntegrity(): void {
  const actualCount = Object.keys(QUALITY_RULES_BY_TYPE).length;
  if (actualCount !== QUALITY_RULE_COUNT) {
    throw new Error(
      `[QA SYSTEM INTEGRITY VIOLATION] Rule count mismatch: ` +
      `expected ${QUALITY_RULE_COUNT}, found ${actualCount}. ` +
      `System state is corrupted. Contact engineering.`
    );
  }
}

/**
 * Validate that a given rule type exists in the taxonomy.
 * Use for defensive programming at boundaries.
 */
export function assertValidRuleType(ruleType: string): asserts ruleType is QualityIssueType {
  if (!(ruleType in QUALITY_RULES_BY_TYPE)) {
    throw new Error(
      `[QA SYSTEM] Unknown rule type "${ruleType}". ` +
      `Valid types: ${ALL_ISSUE_TYPES.join(', ')}`
    );
  }
}
